import { createSubjectApi } from "@/api/services/admin/learning-content";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { TopPage } from "@/components/elements/TopPage";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { STATUS_LABEL } from "@/constants/enum";
import { BasicInfoForm } from "@/features/learning-content/components/SubjectForm/BasicInfoForm";

import { TextExplanationForm } from "@/features/learning-content/components/SubjectForm/TextExplanationForm";
import { WordProblemDataForm } from "@/features/learning-content/components/SubjectForm/WordProblemDataForm";
import { useIndustryDetail } from "@/features/learning-content/hooks/useIndustryDetail";
import { subjectSchema } from "@/features/learning-content/schema/SubjectSchema";
import { TSubjectCreateForm } from "@/features/learning-content/types/learningContent";
import { useToast } from "@/hooks/useToast";
import { AppRoutes } from "@/routes/config";
import { showError } from "@/utils/error";
import { getDynamicRoute } from "@/utils/url";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Stack, Typography, styled } from "@mui/material";
import { FC, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

type TSubjectCreatePageProps = {};

const INIT_FORM_DATA: TSubjectCreateForm = {
  subjectName: "",
  internalNote: null,
  status: STATUS_LABEL.DISABLED,
  activationDate: "--/--/--",
  vocabularySpreadsheetUrl: "",
  problemSpreadsheetUrl: "",
  problemScoreAllocation: "",
  sectionDivision: "none",
  mockExamQuestionCount: "",
  fullScore: "",
  passingLineQuestionCount: "",
  passingScore: "",
  textExplanationFile: null,
  textExplanationPageCount: 0,
  textExplanationInternalNote: null,
};

// Subject create page - creates new subject under an industry/category
export const SubjectCreatePage: FC<TSubjectCreatePageProps> = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const { data: industryData } = useIndustryDetail(id);
  const industryName = industryData?.name ?? "";

  const methods = useForm<TSubjectCreateForm, object, TSubjectCreateForm>({
    defaultValues: INIT_FORM_DATA,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<TSubjectCreateForm>(subjectSchema as any),
    mode: "all",
    reValidateMode: "onBlur",
  });

  const watchedName = methods.watch("subjectName");
  const displayName = watchedName || "(WEEK名未入力)";

  const onSubmit = async (data: TSubjectCreateForm) => {
    if (!id) return;

    setLoading(true);
    try {
      await createSubjectApi({
        industryId: Number(id),
        name: data.subjectName,
        isActive: false,
        internalMemo: data.internalNote || null,
        vocabularySpreadsheetUrl: data.vocabularySpreadsheetUrl || null,
        questionSpreadsheetUrl: data.problemSpreadsheetUrl || null,
        questionScore: data.problemScoreAllocation ? Number(data.problemScoreAllocation) : null,
        partType: data.sectionDivision || null,
        mockExamQuestionCount: data.mockExamQuestionCount ? Number(data.mockExamQuestionCount) : null,
        passingLineQuestionCount: data.passingLineQuestionCount ? Number(data.passingLineQuestionCount) : null,
        totalScore: data.fullScore ? Number(data.fullScore) : null,
        passingScore: data.passingScore ? Number(data.passingScore) : null,
        textbookFileUrl: data.textExplanationFile?.filePath || null,
        textbookPageCount: data.textExplanationPageCount || null,
        textbookMemo: data.textExplanationInternalNote || null,
      });

      toast.success(API_SUCCESS_MESSAGE);
      navigate(getDynamicRoute(AppRoutes.industryDetail, { id }));
    } catch (error) {
      showError(error);
    }
    setLoading(false);
  };

  const handleBack = () => {
    if (id) {
      navigate(getDynamicRoute(AppRoutes.industryDetail, { id }));
    } else {
      navigate(AppRoutes.learningContent);
    }
  };

  return (
    <EditWrapper gap={4}>
      <TopPage
        title={
          <Typography variant="head32Bold" display="flex" alignItems="center" gap={2}>
            {industryName}-新規WEEKの登録
          </Typography>
        }
        description={
          <Typography variant="body14">
            新しいWEEKを追加します。コンテンツ投入、カリキュラムの順序変更、有効化などは登録後から対応できます。
          </Typography>
        }
        enablePageBack={false}
      />
      <Form methods={methods} onSubmit={onSubmit} schema={subjectSchema} initialValues={INIT_FORM_DATA}>
        <MenuNavigate
          menuPrefix={
            <Box sx={{ p: "16px 16px 12px 16px" }}>
              <Typography variant="body14" sx={{ fontWeight: 700, color: "text.primary", fontSize: "14px" }}>
                {displayName}
              </Typography>
            </Box>
          }
          contents={[
            {
              title: "基本情報",
              content: <BasicInfoForm />,
            },
            {
              title: "単語・問題データ",
              content: <WordProblemDataForm />,
            },
            {
              title: "テキスト解説",
              content: <TextExplanationForm />,
            },
          ]}
          contentWidth="900px"
          contentAffix={
            <FooterContainer>
              <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
                <Button size="sm-round" variant="secondary" onClick={handleBack}>
                  戻る
                </Button>
                <Button disabled={loading} size="sm-round" type="submit">
                  保存
                </Button>
              </Stack>
            </FooterContainer>
          }
        />
      </Form>
    </EditWrapper>
  );
};

const EditWrapper = styled(Stack)`
  width: 1198px;
  margin: 0 auto;
  margin-top: 8px;
`;
